import {
  DealBuilderFormValues,
  DealBuilderStep,
  DealBuilderSummaryItemConfig,
  DealSummaryCardTypes,
  SendVia,
  StepConfig,
} from '../types';
import { EquipmentCondition, QuoteDepositType } from '@trnsact/trnsact-shared-types';
import { SelectOption } from 'global';
import { SaleType } from 'modules/desking';
import { NotificationInput } from '../api';

// Matches the palette used for contact avatars elsewhere in the app (see modules/dealActivity)
export const CONTACT_AVATAR_COLORS = ['#E1BEE7', '#BBDEFB', '#B2DFDB', '#FFE0B2', '#F8BBD0', '#C8E6C9'];

export const STEP_CONFIG: StepConfig[] = [
  { step: DealBuilderStep.Contacts, label: 'Contacts' },
  { step: DealBuilderStep.Equipment, label: 'Equipment' },
  { step: DealBuilderStep.Pricing, label: 'Pricing' },
  { step: DealBuilderStep.FinanceMenu, label: 'Finance & Menu', subLabel: '(Recommended)' },
  { step: DealBuilderStep.ReviewSend, label: 'Review & Send' },
];

// Matches the design: show at most 2 unselected recipients besides the primary contact
export const MAX_ADDITIONAL_RECIPIENTS = 2;

export const DEAL_BUILDER_SUMMARY_ITEMS: DealBuilderSummaryItemConfig[] = [
  { key: 'hardCost', label: 'Hard Cost', color: 'default' },
  { key: 'feesAndTaxes', label: 'Fees & Taxes', color: 'default' },
  { key: 'customerDeposit', label: 'Customer Deposit', color: 'secondary' },
  { key: 'tradeIn', label: 'Trade-In', color: 'warning' },
  { key: 'balanceDue', label: 'Balance Due', color: 'info' },
];

export const DEAL_BUILDER_DEFAULT_VALUES: DealBuilderFormValues = {
  contacts: {
    ownerPgIds: [],
    accountContactIds: [],
  },
  equipment: {
    selectedId: null,
  },
  pricing: {
    lineItems: [],
    customerDeposit: { type: QuoteDepositType.Flat, value: 0, notes: '' },
    tradeIn: [],
  },
  financeMenu: { isEnabled: false, saleType: SaleType.Financing },
  reviewSend: {
    sendVia: SendVia.Email,
    sendEmailTo: [],
    ccEmails: [],
    notesToBuyer:
      'This quote reflects current pricing and coverage options available as of today. All coverage terms, deductibles, and add-on services are listed for your review. Please ensure that all selected options meet your operational needs before finalizing. If you have any questions about coverage details or payment terms, let us know so we can provide the relevant documentation.',
  },
};

export const CUSTOMER_DEPOSIT_TYPE_OPTIONS = [
  { label: 'Percentage', value: QuoteDepositType.Percentage },
  { label: 'Flat Amount', value: QuoteDepositType.Flat },
];

export const EMPTY_TRADE_IN_ROW: DealBuilderFormValues['pricing']['tradeIn'][number] = {
  make: null,
  model: null,
  year: null,
  netTrade: 0,
  mileage: null,
  amountOwed: 0,
  tradeInValue: 0,
  equipmentId: null,
  actualCashValue: 0,
  serialNumberVin: null,
  overUnderAllowance: 0,
  newUsed: EquipmentCondition.Used,
  payoffLienholder: null,
  payoffAccountNumber: null,
  payoffGoodThroughDate: null,
};

// Reasonable equipment model-year range for the Trade-In year dropdown
const TRADE_IN_YEAR_RANGE = 30;
export const TRADE_IN_YEAR_OPTIONS: SelectOption[] = Array.from({ length: TRADE_IN_YEAR_RANGE }, (_, index) => {
  const year = String(new Date().getFullYear() + 1 - index);
  return { label: year, value: year };
});

// Kept separate from the tab's own `#hash` (see TabsContainer) so both can coexist in the URL
export const DEAL_BUILDER_QUERY_PARAM_STEP = 'dbStep';
export const DEAL_BUILDER_QUERY_PARAM_PROGRESS = 'dbProgress';

// Deep-link params consumed by EquipmentStep to auto-open the create/edit equipment modal
export const DEAL_BUILDER_QUERY_PARAM_EQUIPMENT_ACTION = 'dbEquipmentAction';
export const DEAL_BUILDER_QUERY_PARAM_EQUIPMENT_ID = 'dbEquipmentId';
export const DEAL_BUILDER_EQUIPMENT_ACTION_ADD = 'add';
export const DEAL_BUILDER_EQUIPMENT_ACTION_EDIT = 'edit';

export const DESKING_SYNC_DEBOUNCE_MS = 1000;

export const SALE_TYPE_OPTIONS = [
  { value: SaleType.Financing, label: 'Financing', description: 'Add loan terms, rates, and payment options' },
  { value: SaleType.Cash, label: 'Cash', description: 'Add aftermarket products only. No financing' },
] as const;

export const sendViaOptions: SelectOption<SendVia>[] = [
  { label: 'Email', value: SendVia.Email },
  { label: 'Text Message', value: SendVia.Text },
  { label: 'Email & Text', value: SendVia.TextAndEmail },
];

export const sendViaToTransport: Record<SendVia, NotificationInput['transport']> = {
  [SendVia.Email]: 'email',
  [SendVia.Text]: 'phone',
  [SendVia.TextAndEmail]: 'textAndEmail',
};

export const DEAL_SUMMARY_CARD_STYLES: Record<
  DealSummaryCardTypes,
  { bgcolor: string; borderColor: string; color: string }
> = {
  equipmentTotal: { bgcolor: 'grey.50', borderColor: 'grey.200', color: 'text.primary' },
  deposit: { bgcolor: '#F9F2FA', borderColor: '#F3E5F5', color: '#BA68C8' },
  balanceDue: { bgcolor: '#E3F2FD', borderColor: '#BBDEFB', color: '#0057B2' },
  estimatedPayment: { bgcolor: '#E8EAF6', borderColor: '#C5CAE9', color: '#303F9F' },
  customerSavings: { bgcolor: 'grey.50', borderColor: 'grey.200', color: 'text.primary' },
  grossProfit: { bgcolor: '#E8F5E9', borderColor: '#C8E6C9', color: '#2E7D32' },
  profitMargin: { bgcolor: '#E8F5E9', borderColor: '#C8E6C9', color: '#2E7D32' },
};
